import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form } from 'react-bootstrap';
import { useMenu } from '../../auth/useMenu';
import {
  sprayService,
  SpraySession,
  SprayProgramRef,
  InventoryTransaction,
  Orchard,
  AttachedSprayInstruction,
  SprayProduct,
  SprayOperator,
  SprayMachine,
  OperatorSpraySessionCombo,
} from '../../services/sprayService';
import IdChip from './IdChip';
import SessionStatusPill from './statusPills/SessionStatusPill';
import AttachedInstructionRow from './AttachedInstructionRow';
import ComboSummaryList from './ComboSummaryList';
import SessionSignoffPanel from './SessionSignoffPanel';
import ComboActionMenu from './ComboActionMenu';
import ComboInstructionCell from './ComboInstructionCell';
import PageHeader from '../shell/PageHeader';
import { DataTable, DataTableColumn, DefinitionList, DataPair, FormSection, Modal } from '../patterns';
import { getTenantInfo } from '../../services/tenantService';
import { getPrimaryOrchard, getPrimaryOrchardName } from '../../utils/spraySessionDisplay';
import { formatMeasure } from '../../utils/formatMeasure';
import { canEditTerminalSpraySession } from '../../utils/spraySessionPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { formatDate, formatDateTime, formatTime } from '../../dateFormat';

interface Props {
  sessionId: string;
  onBack: () => void;
  onEdit?: (session: SpraySession) => void;
}

const SpraySessionDetail: React.FC<Props> = ({ sessionId, onBack, onEdit }) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<SpraySession | null>(null);
  const [combos, setCombos] = useState<OperatorSpraySessionCombo[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [products, setProducts] = useState<SprayProduct[]>([]);
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [farmTimezone, setFarmTimezone] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<AttachedSprayInstruction | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [viewError, setViewError] = useState('');
  // Phase 5 / Task 5.5 — `spraying:sessions` gates the manager-only Generate
  // PDFs batch button and the per-combo ComboActionMenu trigger. The
  // SessionSignoffPanel self-gates on the same menu, so we don't need to
  // wrap it here.
  const hasManagerMenu = useMenu('spraying:sessions');
  // Role-gated Edit button visibility
  // (track spray_session_ui_changes_20260502 / Phase 1 / Task 4): the
  // Overview surface only allows Farm Administrators and Super Admins to
  // jump back into Edit. Non-admins on terminal-ish sessions see no
  // Edit button at all.
  const { user } = useAuth();
  const canEdit = canEditTerminalSpraySession(user);
  // Phase 5 / Task 5.5 — batch "Generate PDFs" feedback state. A success or
  // error message is rendered inline below the page header; we deliberately
  // do not introduce a toast library here. The status is reset on the next
  // click.
  const [generatingPdfs, setGeneratingPdfs] = useState(false);
  const [pdfStatusMessage, setPdfStatusMessage] = useState<
    { kind: 'success' | 'error'; text: string } | null
  >(null);
  // PDF auto-gen — track in-flight manual regen/delete calls so the cell
  // can optimistically render the spinner state without waiting for the
  // backend to flip regenStatus to 'regenerating'.
  const [inFlightComboActions, setInFlightComboActions] = useState<Set<string>>(new Set());

  const reloadSession = useCallback(() => {
    return sprayService.getSession(sessionId)
      .then(({ session: s, combos: c, consumptionTransactions }) => {
        setSession(s);
        // Defensive default — older API shapes may not ship combos.
        setCombos(c ?? []);
        setTransactions(consumptionTransactions);
      })
      .catch((err: any) => setError(err.message ?? t('spray.sessions.loadError')));
  }, [sessionId, t]);

  useEffect(() => {
    reloadSession().finally(() => setLoading(false));
  }, [reloadSession]);

  // PDF auto-gen — optimistic in-flight helpers.
  const startComboAction = (comboId: string) => {
    setInFlightComboActions(prev => {
      const next = new Set(prev);
      next.add(comboId);
      return next;
    });
  };
  const endComboAction = (comboId: string) => {
    setInFlightComboActions(prev => {
      const next = new Set(prev);
      next.delete(comboId);
      return next;
    });
  };

  // PDF auto-gen — live polling while any combo is in the 'regenerating' state.
  // The interval clears as soon as all combos flip to 'idle' or 'failed'.
  useEffect(() => {
    const anyRegenerating = combos.some(c => c.regenStatus === 'regenerating');
    if (!anyRegenerating) return;
    const id = setInterval(() => {
      void reloadSession();
    }, 2500);
    return () => clearInterval(id);
  }, [combos, reloadSession]);

  useEffect(() => {
    sprayService.getOrchards().then(setOrchards).catch(() => {});
  }, []);

  useEffect(() => {
    // Catalog lookup for the consumption-transactions table unit suffix
    // (Phase 4 / Task 4.2). Failure here is silent — the cell falls
    // back to a bare quantity number with no separator.
    sprayService.getProducts().then(setProducts).catch(() => {});
  }, []);

  // Phase 5 / Task 5.5 — operator + machine catalogs feed ComboSummaryList's
  // id → display-name lookups. Failures are silent; the component
  // falls back to "Unknown operator" / "Unknown machine".
  useEffect(() => {
    sprayService.getOperators().then(setOperators).catch(() => {});
  }, []);

  useEffect(() => {
    sprayService.getMachines().then(setMachines).catch(() => {});
  }, []);

  useEffect(() => {
    // Tenant timezone for humanised instruction history timestamps. Failure
    // here is not user-facing — the helper falls back to browser locale.
    getTenantInfo().then(info => {
      if (info && typeof info.farmTimezone === 'string') setFarmTimezone(info.farmTimezone);
    }).catch(() => {});
  }, []);

  // Breadcrumb leaf falls back to the list title until the session loads,
  // then resolves to the session-specific heading. Hook must run on every
  // render, so the conditional is on the label content, not the call.
  const breadcrumbLeaf = session
    ? t('spray.sessions.sessionHeading', { name: session.name ?? formatDate(session.date, { timeZone: 'UTC' }) })
    : t('spray.sessions.title');
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.sessions.title'), to: '/protection/sessions' },
    { label: breadcrumbLeaf },
  ]);

  // Multi-operator session discriminator. `legacyAttribution === 'single-operator'`
  // marks data migrated from the pre-Phase-1 single-operator model — those rows
  // still rely on the session-level `startDate`/`endDate` (Begin/End Time are
  // derived from these, not stored separately — the legacy strings were
  // removed in #162), `wind/temperature/rain`, and `ppeChecklist` fields. New
  // multi-operator sessions leave these unpopulated and store the same data
  // per-combo (rendered by ComboSummaryList).
  const isLegacySingleOperator = session?.legacyAttribution === 'single-operator';
  const isMultiOperator = !isLegacySingleOperator && combos.length > 0;

  // Derive session-level start/end from combos for multi-operator sessions.
  // Earliest startedAt and latest endedAt across non-cancelled combos give a
  // sensible session-level "wall clock window" for the detail summary.
  const derivedSessionWindow = useMemo(() => {
    if (!isMultiOperator) return { start: null, end: null };
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    for (const c of combos) {
      if (c.status === 'cancelled') continue;
      if (c.startedAt && (!minStart || c.startedAt < minStart)) minStart = c.startedAt;
      if (c.endedAt && (!maxEnd || c.endedAt > maxEnd)) maxEnd = c.endedAt;
    }
    return { start: minStart, end: maxEnd };
  }, [isMultiOperator, combos]);

  // Aggregate consumption from combos.productsConsumed (rolled up by productId).
  // For multi-operator sessions this is the only reliable source — the
  // SprayInventoryTransaction rows that ride along on the session are created
  // by the planning-time post-save hook with `quantity: 0` and never get
  // recomputed when combos complete. Sourcing the table from combos gives
  // both the actual consumed quantity and an obvious place to attach a
  // product-name column.
  const aggregatedConsumption = useMemo(() => {
    if (!isMultiOperator) return null;
    const byProduct = new Map<string, { productId: string; quantity: number; quantityUnit?: string }>();
    for (const c of combos) {
      for (const p of c.productsConsumed ?? []) {
        const existing = byProduct.get(p.productId);
        byProduct.set(p.productId, {
          productId: p.productId,
          quantity: (existing?.quantity ?? 0) + p.quantity,
          // Carry the row's own consumption unit through the rollup. Rows for
          // one product share a unit, so the first non-empty one wins.
          quantityUnit: existing?.quantityUnit ?? p.quantityUnit,
        });
      }
    }
    return Array.from(byProduct.values());
  }, [isMultiOperator, combos]);

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
  if (!session) return null;

  const primaryOrchardName = getPrimaryOrchardName(session, orchards);
  const primaryOrchard = getPrimaryOrchard(session, orchards);
  const petalFallDate = primaryOrchard?.activePetalFallDate
    ? formatDate(primaryOrchard.activePetalFallDate, { timeZone: 'UTC' })
    : null;
  const programName = typeof session.programId === 'object' && session.programId !== null
    ? (session.programId as SprayProgramRef).name
    : null;
  const sessionHeadingName = session.name ?? formatDate(session.date, { timeZone: 'UTC' });

  // Attached Instructions list. Server-populated as an array of
  // AttachedSprayInstruction objects (see sprayService.ts) when the
  // session has at least one entry; absent / empty otherwise. We
  // sort newest-first defensively even though the backend already
  // sorts by createdAt desc.
  const attached = (session.instructionIds ?? [])
    .filter((x): x is AttachedSprayInstruction => typeof x !== 'string')
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const resolveProductName = (productId: string): string => {
    const p = products.find((x) => x._id === productId);
    return p?.productName ?? productId;
  };

  // Legacy single-operator path: render the InventoryTransaction rows that
  // were created at session-level by the SpraySession post-save hook.
  const legacyConsumptionColumns: DataTableColumn<InventoryTransaction>[] = [
    {
      key: 'product',
      label: t('spray.inventory.product'),
      render: (tx) => resolveProductName(tx.productId),
    },
    {
      key: 'type',
      label: t('spray.inventory.type'),
      render: (tx) => t(`spray.inventory.${tx.type}`),
    },
    {
      key: 'quantity',
      label: t('spray.inventory.quantity'),
      // Label with the ledger row's OWN quantityUnit (falls back to a bare
      // number when absent), and rescale to a readable unit via formatMeasure
      // (18000 ml -> 18 L). Must NOT use the product-catalog dosePer100LUnit,
      // which can disagree with the unit the quantity is actually stored in.
      render: (tx) => formatMeasure(tx.quantity, tx.quantityUnit),
    },
    {
      key: 'date',
      label: t('spray.inventory.date'),
      render: (tx) => formatDate(tx.date, { timeZone: 'UTC' }),
    },
  ];

  // Multi-operator path: aggregate consumption rolled up from every
  // combo's productsConsumed. Carries product NAME (column) + unit
  // (alongside quantity) so the table no longer looks like a wall of
  // "Consumption" rows with zero values.
  const aggregateConsumptionColumns: DataTableColumn<{ productId: string; quantity: number; quantityUnit?: string }>[] = [
    {
      key: 'product',
      label: t('spray.inventory.product'),
      render: (row) => resolveProductName(row.productId),
    },
    {
      key: 'quantity',
      label: t('spray.inventory.quantity'),
      // Rolled-up row carries its own quantityUnit (see aggregatedConsumption);
      // formatMeasure rescales to a readable unit (1500 g -> 1.5 kg).
      render: (row) => formatMeasure(row.quantity, row.quantityUnit),
    },
  ];

  // Task 18 — per-combo PDF cell handlers. Errors surface via the existing
  // pdfStatusMessage inline banner (same mechanism as the batch Generate PDFs
  // button so no new state is needed).
  const handleComboView = async (comboId: string) => {
    const combo = combos.find(c => c._id === comboId);
    if (!combo?.instructionId) return;
    try {
      const url = await sprayService.getInstructionSignedUrl(
        typeof combo.instructionId === 'string' ? combo.instructionId : (combo.instructionId as any)._id,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setPdfStatusMessage({ kind: 'error', text: err.message || t('spray.combo.pdf.viewFailed') });
    }
  };

  const handleComboRegenerate = async (comboId: string) => {
    startComboAction(comboId);
    try {
      const { pdfUrl } = await sprayService.regenerateComboInstruction(comboId);
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      await reloadSession();
    } catch (err: any) {
      setPdfStatusMessage({ kind: 'error', text: err.message || t('spray.combo.pdf.regenFailed') });
    } finally {
      endComboAction(comboId);
    }
  };

  const handleComboDelete = async (comboId: string) => {
    startComboAction(comboId);
    try {
      await sprayService.deleteComboInstruction(comboId);
      await reloadSession();
    } catch (err: any) {
      setPdfStatusMessage({ kind: 'error', text: err.message || t('spray.combo.pdf.deleteFailed') });
    } finally {
      endComboAction(comboId);
    }
  };

  return (
    <div data-testid="spray-session-detail">
      <PageHeader
        title={t('spray.sessions.sessionHeading', { name: sessionHeadingName })}
        meta={
          <span className="d-inline-flex align-items-center flex-wrap gap-2">
            <IdChip prefix="SES" seq={session.seq} />
            {session.status && <SessionStatusPill status={session.status} />}
            {programName && (
              <span data-testid="program-meta">
                {t('spray.sessions.programHeading', { name: programName })}
              </span>
            )}
          </span>
        }
        actions={
          <span className="d-inline-flex gap-2">
            {onEdit && canEdit && (
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                onClick={() => onEdit(session)}
              >
                {t('common.edit')}
              </button>
            )}
            {hasManagerMenu &&
              !['draft', 'cancelled', 'deleted'].includes(session.status ?? '') && (
                <button
                  type="button"
                  className="pat-btn pat-btn--secondary"
                  disabled={generatingPdfs}
                  onClick={async () => {
                    setGeneratingPdfs(true);
                    setPdfStatusMessage(null);
                    try {
                      const results = await sprayService.generateSessionInstruction(session._id);
                      setPdfStatusMessage({
                        kind: 'success',
                        text: t('spray.sessions.generatePdfs.successCount', {
                          count: results.length,
                        }),
                      });
                      await reloadSession();
                    } catch (err: any) {
                      setPdfStatusMessage({
                        kind: 'error',
                        text: `${t('spray.sessions.generatePdfs.errorPrefix')}${
                          err?.message ?? t('common.error')
                        }`,
                      });
                    } finally {
                      setGeneratingPdfs(false);
                    }
                  }}
                >
                  {t('spray.sessions.generatePdfs.button')}
                </button>
              )}
            <button type="button" className="pat-btn pat-btn--subtle" onClick={onBack}>
              {t('common.back')}
            </button>
          </span>
        }
      />

      {pdfStatusMessage && (
        <Alert
          variant={pdfStatusMessage.kind === 'success' ? 'success' : 'danger'}
          className="mt-2"
          dismissible
          onClose={() => setPdfStatusMessage(null)}
        >
          {pdfStatusMessage.text}
        </Alert>
      )}

      <div className="pat-form-shell pat-form-shell--short">
        <div className="pat-form-shell__content">
          <FormSection id="detail-summary" title={t('spray.sessions.detailSections.summary')}>
            <DefinitionList columns={2}>
              {primaryOrchardName && (
                <DataPair
                  label={t('spray.sessions.orchard')}
                  value={
                    <>
                      {primaryOrchardName}
                      {petalFallDate && (
                        <span className="text-muted small ms-2">
                          ({t('orchardManager.petalFallDate')}: {petalFallDate})
                        </span>
                      )}
                    </>
                  }
                />
              )}

              <DataPair label={t('spray.sessions.date')} value={formatDate(session.date, { timeZone: 'UTC' })} />

              {/* Multi-operator: render session-level window derived from
                  combos. Legacy single-operator: keep showing the
                  session-level startDate/endDate, with Begin/End Time
                  derived from them via formatTime. */}
              {isMultiOperator ? (
                <>
                  <DataPair
                    label={t('spray.sessions.startDate')}
                    value={
                      derivedSessionWindow.start
                        ? formatDateTime(derivedSessionWindow.start, { timeZone: farmTimezone })
                        : '—'
                    }
                  />
                  <DataPair
                    label={t('spray.sessions.endDate')}
                    value={
                      derivedSessionWindow.end
                        ? formatDateTime(derivedSessionWindow.end, { timeZone: farmTimezone })
                        : '—'
                    }
                  />
                </>
              ) : (
                <>
                  {session.startDate && (
                    <DataPair label={t('spray.sessions.startDate')} value={formatDate(session.startDate, { timeZone: 'UTC' })} />
                  )}
                  <DataPair
                    label={t('spray.sessions.beginTime')}
                    value={session.startDate ? formatTime(session.startDate) : '—'}
                  />
                  {session.endDate && (
                    <DataPair label={t('spray.sessions.endDate')} value={formatDate(session.endDate, { timeZone: 'UTC' })} />
                  )}
                  <DataPair
                    label={t('spray.sessions.endTime')}
                    value={session.endDate ? formatTime(session.endDate) : '—'}
                  />
                </>
              )}
            </DefinitionList>
          </FormSection>

          <FormSection
            id="detail-combos"
            title={t('spray.sessions.detailSections.combos')}
          >
            <ComboSummaryList
              combos={combos}
              operators={operators}
              machines={machines}
              products={products}
              parentSession={session}
              legacyAttribution={session.legacyAttribution}
              renderRowActions={
                hasManagerMenu
                  ? (combo) => (
                      <span className="d-inline-flex align-items-center gap-2">
                        <ComboInstructionCell
                          combo={{
                            ...combo,
                            // PDF auto-gen — optimistic spinner: while a manual
                            // regen or delete is in flight, synthesise 'regenerating'
                            // so the cell shows the spinner immediately without
                            // waiting for the backend to flip its own regenStatus.
                            regenStatus: inFlightComboActions.has(combo._id)
                              ? 'regenerating'
                              : combo.regenStatus,
                          }}
                          parentStatus={session.status ?? 'planned'}
                          onView={handleComboView}
                          onRegenerate={handleComboRegenerate}
                          onDelete={handleComboDelete}
                        />
                        <ComboActionMenu
                          combo={combo}
                          onComboChanged={() => reloadSession()}
                        />
                      </span>
                    )
                  : undefined
              }
            />
          </FormSection>

          <SessionSignoffPanel
            session={session}
            onSessionUpdated={(updated) => { setSession(updated); }}
          />

          {/* Legacy session-level Conditions panel. New multi-operator
              sessions store weather per-combo; ComboSummaryList renders
              the per-combo weatherSnapshots on row expand, so this
              panel is hidden to avoid showing a row of "—" placeholders. */}
          {!isMultiOperator && (
            <FormSection id="detail-conditions" title={t('spray.sessions.detailSections.conditions')}>
              <DefinitionList columns={2}>
                <DataPair label={t('spray.sessions.wind')} value={session.wind ?? '—'} />
                <DataPair label={t('spray.sessions.temperature')} value={session.temperature != null ? `${session.temperature}°C` : '—'} />
                <DataPair label={t('spray.sessions.rain')} value={session.rain ? t('common.yes') : t('common.no')} />
              </DefinitionList>
            </FormSection>
          )}

          {session.comments && (
            <FormSection id="detail-comments" title={t('spray.sessions.detailSections.comments')}>
              <DefinitionList columns={2}>
                <DataPair label={t('spray.sessions.comments')} value={session.comments} />
              </DefinitionList>
            </FormSection>
          )}

          {/* Legacy session-level PPE checklist. Per-combo PPE under
              combos[i].ppeChecklist is rendered by ComboSummaryList on
              row expand, so we suppress this section for multi-operator
              sessions to avoid an always-unchecked decoy row. */}
          {!isMultiOperator && session.ppeChecklist && session.ppeChecklist.length > 0 && (
            <FormSection id="detail-ppe" title={t('spray.sessions.detailSections.ppe')}>
              <div>
                {session.ppeChecklist.map((entry, idx) => (
                  <Form.Check
                    key={idx}
                    id={`ppe-detail-${idx}`}
                    type="checkbox"
                    label={entry.item}
                    checked={entry.checked}
                    readOnly
                  />
                ))}
              </div>
            </FormSection>
          )}

          <FormSection id="detail-consumption" title={t('spray.sessions.detailSections.consumption')}>
            {isMultiOperator && aggregatedConsumption ? (
              <DataTable<{ productId: string; quantity: number }>
                columns={aggregateConsumptionColumns}
                rows={aggregatedConsumption}
                rowKey={(row) => row.productId}
                emptyState={<p className="text-muted mb-0">—</p>}
              />
            ) : (
              <DataTable<InventoryTransaction>
                columns={legacyConsumptionColumns}
                rows={transactions}
                rowKey={(tx) => tx._id}
                emptyState={<p className="text-muted mb-0">—</p>}
              />
            )}
          </FormSection>

          {attached.length > 0 && (
            <FormSection id="detail-attachments" title={t('spray.sessions.detailSections.attachments')}>
              <div>
                {attached.map(inst => (
                  <AttachedInstructionRow
                    key={inst._id}
                    instruction={inst}
                    farmTimezone={farmTimezone}
                    fetchSignedUrl={(id) => sprayService.getInstructionSignedUrl(id)}
                    onDelete={(target) => {
                      setDeleteError('');
                      setDeleteTarget(target);
                    }}
                    onViewError={setViewError}
                  />
                ))}
              </div>
            </FormSection>
          )}
        </div>
      </div>

      <Modal
        show={deleteTarget !== null}
        onHide={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError('');
        }}
        title={t('spray.sessions.deleteInstructionTitle')}
      >
        <Modal.Body>
          <p>{t('spray.sessions.deleteInstructionWarning')}</p>
          {deleteError && (
            <Alert variant="danger" className="mb-0">{deleteError}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteError('');
            }}
            disabled={deleting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            disabled={deleting}
            onClick={async () => {
              if (!deleteTarget) return;
              setDeleting(true);
              setDeleteError('');
              try {
                await sprayService.deleteSessionInstruction(sessionId, deleteTarget._id);
                setDeleteTarget(null);
                await reloadSession();
              } catch (err: any) {
                setDeleteError(err.message ?? t('common.error'));
              } finally {
                setDeleting(false);
              }
            }}
          >
            {t('common.delete')}
          </button>
        </Modal.Footer>
      </Modal>

      {viewError && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setViewError('')}>
          {viewError}
        </Alert>
      )}
    </div>
  );
};

export default SpraySessionDetail;
